import { Stack, Text } from "@chakra-ui/react";
import { SimpleList } from "@/components/list";

export const FAQS = [
  {
    title: "What are the advantages of implementing Wi-Fi 6 in a business environment?",
    content:
      "Wi-Fi 6 offers several benefits, including higher data transfer rates, increased capacity to handle multiple devices simultaneously, reduced latency, and improved performance in environments with dense device populations. These enhancements make it ideal for modern business applications and IoT integrations.",
  },
  {
    title: "How does WPA3 encryption enhance wireless network security?",
    content:
      "WPA3 provides stronger data protection by offering more robust password-based authentication and individualized data encryption for personal and enterprise networks. It safeguards against brute-force attacks and ensures that data transmitted over the network remains confidential.",
  },
  {
    title:
      "What considerations are essential when designing a wired network for a data center?",
    content: (
      <Stack>
        <Text>Key considerations include: </Text>
        <SimpleList
          points={[
            "Scalability: Ensuring the network can grow with increasing data demands.",
            "Redundancy: Implementing multiple pathways to prevent downtime.",
            "High Bandwidth: Supporting large volumes of data transfer.",
            "Security Measures: Protecting sensitive information through firewalls and access controls.",
          ]}
        />
      </Stack>
    ),
  },
  {
    title: "How can seamless roaming benefit a mobile workforce?",
    content:
      "Seamless roaming allows employees to move throughout a facility or campus without experiencing dropped connections or interruptions in service. This continuous connectivity is crucial for maintaining productivity, especially for tasks requiring constant network access.",
  },
  {
    title: "What steps are involved in optimizing an existing wireless network?",
    content: (
      <Stack>
        <Text>Optimization involves: </Text>
        <SimpleList
          points={[
            "Conducting a Site Survey: Assessing the physical environment for potential interference.",
            "Adjusting Access Point Placement: Ensuring optimal coverage and signal strength.",
            "Updating Firmware: Keeping devices current with the latest performance and security updates.",
            "Monitoring Network Performance: Using tools to track usage patterns and identify bottlenecks.",
          ]}
        />
      </Stack>
    ),
  },
  {
    title: "Why is network segmentation important in wired networks?",
    content:
      "Network segmentation divides a network into smaller, isolated sections, enhancing security by limiting access to sensitive data and reducing the potential spread of breaches. It also improves performance by reducing congestion and allows for more efficient management of network resources.",
  },
  {
    title:
      "How does OneMind Services approach the deployment of IoT-ready wireless infrastructure?",
    content: (
      <Stack>
        <Text>
          OneMind Services designs and implements wireless networks with the capacity to
          support large-scale IoT deployments, ensuring:{" "}
        </Text>
        <SimpleList
          points={[
            "Optimized bandwidth allocation for IoT devices without impacting critical business applications.",
            "Enhanced security measures such as network segmentation, WPA3 encryption, and zero-trust access controls.",
            "Scalability to support thousands of connected devices across enterprise environments, smart buildings, and industrial settings.",
          ]}
        />
      </Stack>
    ),
  },
  {
    title: "What is the difference between 2.4 GHz and 5 GHz Wi-Fi networks?",
    content: (
      <Stack>
        <Stack gap={1}>
          <Text> 2.4 GHz: </Text>
          <SimpleList
            points={[
              "Offers better range and wall penetration but slower speeds.",
              "Suitable for IoT devices, basic browsing, and legacy wireless equipment.",
            ]}
          />
        </Stack>
        <Stack gap={1}>
          <Text> 5 GHz: </Text>
          <SimpleList
            points={[
              "Provides faster speeds and lower interference but has shorter range.",
              "Ideal for video streaming, real-time collaboration, and high-bandwidth applications.",
            ]}
          />
        </Stack>
        <Text>
          Wi-Fi 6 (802.11ax) enhances both 2.4 GHz and 5 GHz bands by improving efficiency
          and allowing more simultaneous connections.
        </Text>
      </Stack>
    ),
  },
  {
    title: "How can network monitoring improve wired and wireless performance?",
    content: (
      <Stack>
        <Text>Proactive network monitoring ensures: </Text>
        <SimpleList
          points={[
            "Real-time detection of bottlenecks and performance issues.",
            "Automated alerts for security threats or unauthorized access.",
            "Optimized bandwidth allocation for critical applications.",
            "Historical analytics to predict and prevent potential outages.",
          ]}
        />
        <Text>
          OneMind Services provides AI-powered network monitoring to enhance operational
          efficiency.{" "}
        </Text>
      </Stack>
    ),
  },
  {
    title: "What factors should businesses consider when upgrading their wired network?",
    content: (
      <Stack>
        <Text>Key factors include: </Text>
        <SimpleList
          points={[
            "Scalability – Can the network handle future growth and increased data demands?",
            "Redundancy – Are there failover mechanisms in place to prevent downtime?",
            "Security – Does the infrastructure support advanced firewall, encryption, and access control policies?",
            "Integration – Can it seamlessly connect with cloud services and wireless networks?",
          ]}
        />
        <Text>
          OneMind Services ensures future proof wired network deployments tailored to
          business needs.{" "}
        </Text>
      </Stack>
    ),
  },
  {
    title: "What is the role of VLANs in wired and wireless networks?",
    content: (
      <Stack>
        <Text>VLANs (Virtual Local Area Networks) improve: </Text>
        <SimpleList
          points={[
            "Security – Isolating sensitive data from unauthorized access.",
            "Performance – Reducing congestion by segmenting traffic.",
            "Network management – Simplifying policy enforcement across departments.",
            "Guest access control – Providing separate Wi-Fi access for employees and visitors.",
          ]}
        />
        <Text>
          OneMind Services implements VLAN configurations to enhance network security and
          efficiency.
        </Text>
      </Stack>
    ),
  },
  {
    title: "How does a hybrid wired-wireless network benefit businesses?",
    content: (
      <Stack>
        <Text>A hybrid infrastructure provides: </Text>
        <SimpleList
          points={[
            "The reliability and speed of wired networks for data-intensive applications.",
            "The flexibility of wireless connectivity for mobile workers and IoT devices.",
            "Improved disaster recovery with multiple connectivity options.",
            "Centralized cloud-based network management for seamless control.",
          ]}
        />
        <Text>
          OneMind Services integrates wired and wireless solutions for high-performance,
          unified networking.
        </Text>
      </Stack>
    ),
  },
  {
    title: "What steps can businesses take to ensure secure guest Wi-Fi access? ",
    content: (
      <Stack>
        <Text>To enhance security on guest Wi-Fi: </Text>
        <SimpleList
          points={[
            "Create a separate network to keep guest traffic isolated from internal resources.",
            "Implement bandwidth limits to prevent overuse and network congestion.",
            "Enable captive portals for authentication and terms of service agreements.",
            "Monitor guest network traffic to detect unusual activity.",
          ]}
        />
        <Text>
          OneMind Services designs secure guest Wi-Fi solutions that balance user
          experience and cybersecurity.
        </Text>
      </Stack>
    ),
  },
];
